import { useCallback, useEffect, useRef } from 'react';
import { useChatContext } from '../context/ChatContext';
import { stripResponseTags, stabilizeMarkdown, dedupeCitations } from '../utils/messageUtils';

// prod = wss://e3uaw17ht4.execute-api.us-east-1.amazonaws.com/prod
// uat = wss://ubyyr1h9eh.execute-api.us-east-1.amazonaws.com/prod
// dev = wss://uex0h62i9j.execute-api.us-east-1.amazonaws.com/prod
const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://e3uaw17ht4.execute-api.us-east-1.amazonaws.com/prod';

export const useWebSocket = () => {
  const {
    dispatch,
    wsRef,
    currentBotMessageRef,
    currentRequestIdRef,
    pendingCitationsRef,
    sessionIdRef,
    isConnected,
    isStreaming
  } = useChatContext();
  
  const pingIntervalRef = useRef(null);

  const handleWebSocketMessage = useCallback((data) => {
    const message = JSON.parse(data);
    console.log('WebSocket message received:', message.type, message);
    
    switch (message.type) {
      case 'message_received':
        break;

      case 'citations_update': {
        const arr = Array.isArray(message.citations) ? message.citations : [];
        pendingCitationsRef.current = arr;

        dispatch({
          type: 'UPDATE_LAST_MESSAGE',
          payload: { citations: dedupeCitations(arr) }
        });
        break;
      }

      case 'stream_start':
        dispatch({ type: 'SET_IS_STREAMING', payload: true });
        console.log('stream_start - creating new bot message');
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: Date.now(),
            position: 'left',
            type: 'text',
            text: '',
            rawText: '',
            citations: [],
            date: new Date(),
            isBot: true,
            isStreaming: true,
            feedback: null,
          }
        });
        break;

      case 'stream_chunk':
        handleStreamChunk(message.content);
        break;

      case 'stream_complete':
        handleStreamComplete(message);
        break;

      case 'query_normalized':
        // optionally: dispatch to show the normalized query somewhere
        // or just ignore
        break;

      case 'stream_end':
        // your server sends stream_end, but you already finalize on stream_complete
        // so safe to ignore (or treat as a “done streaming” signal)
        break;

      case 'stopped':
        dispatch({ type: 'SET_IS_STREAMING', payload: false });
        dispatch({
          type: 'UPDATE_LAST_MESSAGE',
          payload: { isStreaming: false }
        });
        break;

      case 'pong':
        // Handle pong response (connection is alive)
        break;

      case 'stream_error':
      case 'error':
        handleStreamError(stripResponseTags(message.error || 'An error occurred'));
        break;

      default:
        console.warn("Unknown message type:", message);
    }
  }, [dispatch, pendingCitationsRef, currentBotMessageRef]);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    
    wsRef.current = new WebSocket(WEBSOCKET_URL);
    
    wsRef.current.onopen = () => {
      dispatch({ type: 'SET_IS_CONNECTED', payload: true });
      
      // Start ping interval
      pingIntervalRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'ping' }));
        }
      }, 60000); // Ping every 60 seconds
    };
    
    wsRef.current.onclose = () => {
      dispatch({ type: 'SET_IS_CONNECTED', payload: false });
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
    
    wsRef.current.onerror = console.error;
    wsRef.current.onmessage = (e) => {
      console.log("WS Message:", e.data);
      handleWebSocketMessage(e.data);
    };
  }, [dispatch, wsRef, handleWebSocketMessage]);

  const handleStreamChunk = (chunk) => {
    if (!chunk) return;
    console.log('handleStreamChunk - received chunk:', chunk);
    
    dispatch({
      type: 'UPDATE_LAST_MESSAGE',
      payload: (lastMessage) => {
        if (!lastMessage?.isBot) return lastMessage;
        
        const currentRaw = lastMessage.rawText || '';
        console.log('handleStreamChunk - current raw text:', currentRaw);
        
        const nextRaw = (lastMessage.rawText || '') + chunk;
        const updatedMessage = {
          ...lastMessage,
          rawText: nextRaw,
          text: stabilizeMarkdown(nextRaw),
          isStreaming: true
        };
        
        console.log('handleStreamChunk - updated text:', updatedMessage.text);

        if (!Array.isArray(updatedMessage.citations) && (!pendingCitationsRef.current || pendingCitationsRef.current.length === 0)) {
          updatedMessage.citations = null;
        }

        currentBotMessageRef.current = updatedMessage;
        return updatedMessage;
      }
    });
  };

  const handleStreamComplete = (payload) => {
    dispatch({
      type: 'UPDATE_LAST_MESSAGE',
      payload: (lastMessage) => {
        if (!lastMessage?.isBot) return lastMessage;
        
        let text = lastMessage.rawText ?? lastMessage.text ?? '';
        let citations = Array.isArray(pendingCitationsRef.current) ? pendingCitationsRef.current : [];

        if (payload && typeof payload === 'object') {
          if (Array.isArray(payload.citations)) {
            citations = payload.citations;
          }

          if (payload.full_response && typeof payload.full_response === 'object') {
            const obj = payload.full_response;
            if (Array.isArray(obj.citations)) citations = obj.citations;
          }

          if (typeof payload.full_response === 'string') {
            try {
              const parsed = JSON.parse(payload.full_response);
              if (parsed && typeof parsed === 'object') {
                if (Array.isArray(parsed.citations)) citations = parsed.citations;
              }
            } catch (err) {
              console.log('full_response not JSON (ok):', err);
            }
          }

          if (typeof payload.full_response_json === 'string') {
            try {
              const parsed = JSON.parse(payload.full_response_json);
              if (parsed && typeof parsed === 'object') {
                if (Array.isArray(parsed.citations)) citations = parsed.citations;
              }
            } catch (err) {
              console.log('full_response_json not JSON (ok):', err);
            }
          }
        }

        citations = dedupeCitations(citations);

        const updatedMessage = {
            ...lastMessage,
            text,
            citations,
            isStreaming: false,
            responseId:
              payload?.responseId ??
              payload?.responseMeta?.responseId ??
              lastMessage.responseId,
            dbTimestamp:
              payload?.timestamp ??
              payload?.responseMeta?.timestamp ??
              lastMessage.dbTimestamp,
          };
        delete updatedMessage.rawText;
        return updatedMessage;
      }
    });

    pendingCitationsRef.current = [];
    dispatch({ type: 'SET_IS_STREAMING', payload: false });
  };

  const handleStreamError = (error) => {
    dispatch({
      type: 'UPDATE_LAST_MESSAGE',
      payload: { text: error, isStreaming: false }
    });
    pendingCitationsRef.current = [];
    dispatch({ type: 'SET_IS_STREAMING', payload: false });
  };

  const sendMessage = (message) => {
    if (!message.trim() || !isConnected || isStreaming) return;
    
    const userMessage = {
      id: Date.now(),
      position: 'right',
      type: 'text',
      text: message,
      date: new Date(),
      isBot: false,
    };
    
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_IS_STREAMING', payload: true });
    
    try {
      const requestId = `req-${Date.now()}`;
      currentRequestIdRef.current = requestId;
      wsRef.current.send(JSON.stringify({
        action: 'user_message',
        requestId,
        message,
        sessionId: sessionIdRef.current
      }));
    } catch {
      handleStreamError('Failed to send message.');
    }
  };

  const stopStream = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const rid = currentRequestIdRef.current;
    if (!rid) return;
    try {
      wsRef.current.send(JSON.stringify({
        action: 'stop_stream',
        requestId: rid,
        sessionId: sessionIdRef.current
      }));
    } catch (e) {
      console.error('Failed to send stop:', e);
    }
  };

  const sendFeedback = (feedbackData) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'submitFeedback',
        data: {
          sessionId: sessionIdRef.current,
          ...feedbackData
        }
      }));
    }
  };

  useEffect(() => {
    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      const timer = setTimeout(connectWebSocket, 100);
      return () => {
        clearTimeout(timer);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [connectWebSocket]);

  useEffect(() => {
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [connectWebSocket]);

  return { sendMessage, stopStream, sendFeedback, isConnected, isStreaming };
};